import { useEffect, useState, useSyncExternalStore } from 'react';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { authRoutes } from '@/shared/app/auth/routes';
import { FullscreenLoader } from '@/shared/components/ui/fullscreen-loader/fullscreen-loader';

// Only the auth routes ship with the entry. The console tree is discovered on the first
// navigation they do not match (the redirect to "/" after sign-in), so the login page
// does not execute the whole shell before it can render the form.
const loadConsoleRoutes = () => import('@/console/app/console-routes');

// Not memoised: React Router drops patch() from an aborted navigation, so an interrupted
// first discovery would leave the console tree unpatched. Identical patches are deduped.
export const createAppRouter = () =>
  createBrowserRouter([authRoutes], {
    patchRoutesOnNavigation: async ({ patch }) => {
      const { consoleRoutes } = await loadConsoleRoutes();
      patch(null, consoleRoutes);
    },
  });

// Warm the console chunk on the first keystroke or pointer press: by then the form has
// painted, and the chunk downloads and executes while the user types and while the Cognito
// round trips are in flight, instead of competing with the form's first render. An idle
// callback is too early here: the page is idle while it waits for the session check.
const prefetchConsoleRoutesOnFirstInteraction = () => {
  const events = ['keydown', 'pointerdown'] as const;
  const prefetch = () => {
    events.forEach((event) => window.removeEventListener(event, prefetch));
    void loadConsoleRoutes();
  };
  events.forEach((event) => window.addEventListener(event, prefetch));

  return () =>
    events.forEach((event) => window.removeEventListener(event, prefetch));
};

export const AppRouter = () => {
  const [router] = useState(createAppRouter);
  // A cold console URL matches nothing until the console chunk is in, and React Router
  // renders nothing for it until the first navigation completes: no hydrate fallback applies.
  const isDiscoveringInitialRoutes = useSyncExternalStore(
    router.subscribe,
    () => !router.state.initialized && router.state.matches.length === 0,
  );

  useEffect(prefetchConsoleRoutesOnFirstInteraction, []);

  return (
    <>
      {isDiscoveringInitialRoutes && <FullscreenLoader />}
      <RouterProvider router={router} />
    </>
  );
};
